import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * FEAT-20260911-PW-019 Layer 4：坐标气泡卡
 *
 * 渲染在父页面（预览容器内绝对定位），不污染试玩产物 DOM 与包体。
 * 复制文案格式与 Prompt 的坐标语言完全对齐（方案 7.5）：
 *   单点：x=45% y=55%（网格 E6）
 *   两点：从 x=20% y=80%（网格 C8）移动到 x=45% y=55%（网格 E6）
 *   多点：路径 x=20% y=80%（C8）→ x=35% y=62%（D7）→ x=45% y=55%（E6）
 */

export interface PickPoint {
  /** 'P1' ... 'P5' */
  id: string;
  /** 格号 'E6' */
  cell: string;
  xPct: number;
  yPct: number;
  /** 语义邻居（Layer 2 FrameSemanticMap 就绪后由产物侧回传） */
  nearest?: { id: string; label: string; distPct?: number } | null;
}

/** 单点文案：x=45% y=55%（网格 E6） */
export function fmtPoint(p: PickPoint): string {
  return `x=${p.xPct}% y=${p.yPct}%（网格 ${p.cell}）`;
}

/** 路径文案（≥2 点） */
export function fmtPath(picks: PickPoint[]): string {
  if (picks.length === 2) return `从 ${fmtPoint(picks[0])} 移动到 ${fmtPoint(picks[1])}`;
  return `路径 ${picks.map((p) => `x=${p.xPct}% y=${p.yPct}%（${p.cell}）`).join(' → ')}`;
}

/** 复制到剪贴板：Clipboard API 失败降级 execCommand */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

export interface CoordPickerAnchor {
  left: number;
  top: number;
  flipX: boolean;
  flipY: boolean;
}

interface CoordPickerProps {
  picks: PickPoint[];
  selectedId: string | null;
  /** 相对预览容器（position:relative 宿主）的锚点坐标；点为容器内实时 rect 推算 */
  anchor: CoordPickerAnchor | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onClearAll: () => void;
  onInsert: (p: PickPoint) => void;
  onCopyPath: () => void;
}

/** 坐标气泡卡 + 多点列表（方案 7.5） */
export function CoordPicker({
  picks, selectedId, anchor, onSelect, onDelete, onClearAll, onInsert, onCopyPath,
}: CoordPickerProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => { setCopied(false); }, [selectedId]);

  const selected = picks.find((p) => p.id === selectedId) ?? null;
  if (!selected || !anchor) return null;

  const handleCopyPoint = async () => {
    const ok = await copyText(fmtPoint(selected));
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div
      data-coord-picker
      className="absolute z-30 w-[236px] rounded-lg border border-neon-cyan/40 bg-card/95 p-2 text-[11px] leading-snug shadow-xl backdrop-blur"
      style={{
        left: anchor.left,
        top: anchor.top,
        transform: `translate(${anchor.flipX ? '-100%' : '0'}, ${anchor.flipY ? '-100%' : '0'})`,
      }}
    >
      {/* 标题行 */}
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono font-semibold text-neon-cyan">
          {selected.id} · {selected.cell} · x={selected.xPct}% y={selected.yPct}%
        </span>
        <button
          onClick={() => onDelete(selected.id)}
          title="删除本点"
          className="rounded p-0.5 text-foreground/60 hover:bg-muted/60 hover:text-foreground"
        >
          <X size={11} />
        </button>
      </div>

      {/* 语义行（语义地图就绪后才显示） */}
      {selected.nearest && (
        <div className="mt-0.5 text-muted-foreground">
          靠近：{selected.nearest.label}（{selected.nearest.id}）
        </div>
      )}

      {/* 操作按钮行 */}
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        <button
          onClick={handleCopyPoint}
          className={cn(
            'rounded bg-muted/60 px-1.5 py-0.5 text-foreground/85 hover:bg-muted hover:text-foreground',
            copied && 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/15',
          )}
        >
          {copied ? '已复制 ✓' : '📋 复制坐标'}
        </button>
        <button
          onClick={() => onInsert(selected)}
          className="rounded bg-muted/60 px-1.5 py-0.5 text-foreground/85 hover:bg-muted hover:text-foreground"
        >
          ⤴ 插入指令
        </button>
        {picks.length >= 2 && (
          <button
            onClick={onCopyPath}
            className="rounded bg-muted/60 px-1.5 py-0.5 text-foreground/85 hover:bg-muted hover:text-foreground"
          >
            🔗 复制为路径
          </button>
        )}
      </div>

      {/* 多点列表（点击行高亮对应标记） */}
      {picks.length > 1 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1 border-t border-border/50 pt-1.5">
          {picks.map((p) => (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={cn(
                'rounded px-1.5 py-0.5 font-mono',
                p.id === selectedId
                  ? 'bg-neon-cyan/20 text-neon-cyan'
                  : 'bg-muted/60 text-foreground/70 hover:text-foreground',
              )}
            >
              {p.cell} · {p.xPct}/{p.yPct}
            </button>
          ))}
          <button
            onClick={onClearAll}
            className="rounded px-1.5 py-0.5 text-foreground/60 hover:bg-destructive/15 hover:text-destructive"
          >
            清除全部
          </button>
        </div>
      )}
    </div>
  );
}
